import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, FileText } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import api from "@/lib/api";

const SLUGS = [
  { key: "privacy", label: "Privacy Policy", path: "/privacy-policy" },
  { key: "voorwaarden", label: "Algemene Voorwaarden", path: "/algemene-voorwaarden" },
  { key: "cookies", label: "Cookiebeleid", path: "/cookiebeleid" },
];

export default function AdminLegalPages() {
  const [pages, setPages] = useState({});
  const [activeKey, setActiveKey] = useState("privacy");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/legal-pages");
    setPages(data || {});
  };

  useEffect(() => {
    load();
  }, []);

  const active = pages[activeKey] || { title: "", html: "", updated_at_iso: "" };

  const setActiveField = (field, val) => {
    setPages((prev) => ({ ...prev, [activeKey]: { ...(prev[activeKey] || {}), [field]: val } }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/legal-pages/${activeKey}`, {
        title: active.title || "",
        html: active.html || "",
      });
      toast.success("Pagina opgeslagen — direct live op de site.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-7">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-semibold text-oc-primary text-[34px] leading-tight">Juridische pagina's</h1>
          <p className="text-oc-textSecondary text-[14px]">Privacy, voorwaarden & cookies — bewerk vrij in HTML rich text. Direct live na opslaan.</p>
        </div>
        <Button data-testid="save-legal" onClick={save} disabled={busy} className="rounded-oc-btn bg-oc-primary hover:bg-oc-secondary text-white px-5">
          <Save className="w-4 h-4 mr-2" /> {busy ? "Opslaan..." : "Opslaan"}
        </Button>
      </div>

      <div className="flex gap-2 border-b border-oc-border">
        {SLUGS.map((s) => (
          <button
            key={s.key}
            data-testid={`tab-${s.key}`}
            onClick={() => setActiveKey(s.key)}
            className={`px-4 py-3 text-[14px] font-semibold border-b-2 transition-colors ${
              activeKey === s.key ? "border-oc-primary text-oc-primary" : "border-transparent text-oc-textSecondary hover:text-oc-primary"
            }`}
          >
            <FileText className="inline w-4 h-4 mr-2" />
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input
              data-testid="legal-title"
              value={active.title || ""}
              onChange={(e) => setActiveField("title", e.target.value)}
              placeholder={SLUGS.find((s) => s.key === activeKey)?.label || ""}
              className="h-12 rounded-oc-btn"
            />
          </div>
          <div className="space-y-2">
            <Label>Inhoud (HTML — rich text editor)</Label>
            <RichTextEditor
              value={active.html || ""}
              onChange={(v) => setActiveField("html", v)}
              placeholder="Begin met typen, of plak HTML. Headings, lijsten, links, vetgedrukt zijn ondersteund."
            />
          </div>
        </div>
        <aside className="space-y-4">
          <div className="bg-white border border-oc-border rounded-oc p-5">
            <p className="text-[12px] uppercase tracking-wide font-semibold text-oc-textSecondary">Live URL</p>
            <p className="font-mono text-[13px] text-oc-primary mt-1 break-all">{SLUGS.find((s) => s.key === activeKey)?.path}</p>
            {active.updated_at_iso && (
              <p className="text-[12px] text-oc-textSecondary mt-3">Laatst bijgewerkt: {new Date(active.updated_at_iso).toLocaleString("nl-BE")}</p>
            )}
          </div>
          <div className="bg-oc-subtle rounded-oc p-5 space-y-2">
            <p className="text-[13px] font-semibold text-oc-primary">Tips</p>
            <ul className="text-[12px] text-oc-textSecondary list-disc pl-4 space-y-1">
              <li>Houd het kort en duidelijk — burgerrechten staan op kop.</li>
              <li>Verwijs altijd naar je e-mail (info@offerteexpert.be) voor klachten.</li>
              <li>Tip: laat een GDPR-jurist deze tekst nakijken vóór je live gaat.</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
